import * as React from 'react';

/**
 * Route-level code splitting.
 *
 * `lazyPage` wraps React.lazy for pages exported by name (the codebase
 * convention), so each page becomes its own chunk loaded on first navigation.
 *
 * IMPORTANT: pass a direct file import (`() => import('.../pages/FooPage')`),
 * never a feature barrel — importing the barrel would pull every page of the
 * feature into a single chunk and defeat the split.
 */
export function lazyPage<T extends Record<string, unknown>>(
  loader: () => Promise<T>,
  exportName: keyof T
): React.LazyExoticComponent<React.ComponentType> {
  return React.lazy(async () => {
    const module = await loader();
    return { default: module[exportName] as React.ComponentType };
  });
}

/**
 * Suspense fallback shown while a page chunk downloads. Quiet by design —
 * chunk loads are usually sub-second, so no attention-grabbing animation.
 * The spinner styles (`.wl-route-spinner`) live in app-shell.css and respect
 * prefers-reduced-motion.
 */
export function RouteFallback() {
  return (
    <div
      role="status"
      aria-label="Loading page"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '40vh',
        width: '100%',
      }}
    >
      <span className="wl-route-spinner" aria-hidden="true" />
    </div>
  );
}
